import React, { useEffect } from 'react'

import { useEditDoor } from '../hooks/useEditDoor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box } from '@chakra-ui/react'

import { SkeletonModalBody } from '@/components/ui'

import { FormDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { DeviceDetailsSection } from './DeviceDetailsSection'
import { ThresholdConfigurationSection } from './ThresholdConfigurationSection'

interface EditDoorFormIProps {
  deviceId: string
  onLoadingChange: (loading: boolean) => void
  onSave?: () => void
}

export const EditDoorForm = ({
  deviceId,
  onLoadingChange,
  onSave,
}: EditDoorFormIProps) => {
  const {
    handleSubmit,
    errors,
    register,
    control,
    formState,
    setValue,
    trigger,
  } = useForm<FormDataTypeI>({
    mode: 'onChange',
  })
  const { shouldEnableUpdateDevice, isInternalImplementer } = usePermissions()
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const shouldShowThresholdPanel = isTraceLogsEnabled && isInternalImplementer
  const { isValid } = formState
  const {
    deviceData,
    isLoading,
    floorsOptions,
    isUpdateDeviceLoading,
    isUpdateDoorLoading,
    updateDoor,
  } = useEditDoor(deviceId)

  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<FormDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location.value,
      manufacturerName: values.manufacturerName,
    }

    try {
      await updateDoor(formData, values.externalId)
      showSuccess('Device updated.')
      onSave?.()
    } catch {
      showError()
    }
  }

  useEffect(() => {
    if (!isLoading) {
      setValue('name', deviceData?.device?.name, { shouldValidate: true })
      setValue(
        'location',
        floorsOptions?.find((i) => i.value === deviceData?.device?.floor?.id),
        {
          shouldValidate: true,
        }
      )
      setValue('externalId', deviceData?.device?.door?.externalId)
      setValue('manufacturerName', deviceData?.device?.manufacturerName)
      trigger(['name'])
    }
  }, [isLoading, floorsOptions])

  useEffect(() => {
    // Update parent component's loading state
    onLoadingChange(isLoading)
  }, [deviceData, isLoading])

  return (
    <Box>
      {!deviceData || isLoading ? (
        <SkeletonModalBody />
      ) : (
        <>
          <DeviceDetailsSection
            control={control}
            errors={errors}
            floorsOptions={floorsOptions}
            isDevicePlaced={isPlaced(deviceData?.device)}
            isLoading={isLoading}
            isSaveDisabled={!isValid || !shouldEnableUpdateDevice}
            isSaveLoading={isUpdateDeviceLoading || isUpdateDoorLoading}
            onSave={handleSubmit(onSubmit)}
            register={register}
          />

          {shouldShowThresholdPanel && (
            <ThresholdConfigurationSection
              deviceId={deviceId}
              onSaveCallback={onSave}
            />
          )}
        </>
      )}
    </Box>
  )
}
